"use client";
import React from "react";
import styles from "./MileageVariantSection.module.scss";
import { Dropdown, DropdownItem } from "@components/atoms/Dropdown";
import { VariantBlock } from "@components/molecules/VariantBlock";
import { useMileageVariantSectionData } from "@hooks/useMileageVariantSection";
import { MileageVariantSectionProps } from "@interfaces/mileage";
import assetLoader from "@utils/internalImageLoder";
import Image from "next/image";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { getCarAvailability } from "@utils/carAvailability";

interface MileageVariantSectionClientWrapperProps {
  mileageVariants: MileageVariantSectionProps["mileageVariants"];
  modelName?: any;
  makeName?: any;
  isSellable?: any;
  cityName?: any;
  fuelTypes: string[];
  transmissionTypes: string[];
  carAvailability?: string;
}

export default function MileageVariantSectionClientWrapper({
  mileageVariants,
  modelName,
  makeName,
  isSellable,
  cityName,
  fuelTypes,
  transmissionTypes,
  carAvailability
}: MileageVariantSectionClientWrapperProps) {
  const {
    selectedFuelType,
    setSelectedFuelType,
    selectedTransmission,
    setSelectedTransmission,
    filteredVariants,
    updateVariantDistance,
    getVariantDistance,
    generateVariantKey,
  } = useMileageVariantSectionData(mileageVariants);

  const handleFuelType = async (type: any) => {
    setSelectedFuelType(type.toLowerCase());

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.MILEAGE_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        selected_value: type,
        car_availability: carAvailability || getCarAvailability(`${isSellable}`),
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
      },
    });
  };

  const handleTransmissionType = async (type: any) => {
    setSelectedTransmission(type.toLowerCase());

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.MILEAGE_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_filter",
        selected_value: type,
        car_availability: carAvailability || getCarAvailability(`${isSellable}`),
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
      },
    });
  };

  return (
    <>
      <div className={styles.filters}>
        <Dropdown
          trigger={
            <div className={styles.dropdownTrigger}>
              {selectedFuelType
                ? selectedFuelType.charAt(0).toUpperCase() +
                  selectedFuelType.slice(1)
                : "Fuel type"}
              <Image
                src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt="Chevron_down"
                width={22}
                height={22}
                loading="lazy"
              />
            </div>
          }
        >
          <DropdownItem onClick={() => setSelectedFuelType(null)}>
            All Fuel Types
          </DropdownItem>
          {fuelTypes.map((type) => (
            <DropdownItem key={type} onClick={() => handleFuelType(type)}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </DropdownItem>
          ))}
        </Dropdown>

        <Dropdown
          trigger={
            <div className={styles.dropdownTrigger}>
              {selectedTransmission
                ? selectedTransmission.charAt(0).toUpperCase() +
                  selectedTransmission.slice(1)
                : "Transmission type"}
              <Image
                src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt="Chevron_down"
                width={22}
                height={22}
                loading="lazy"
              />
            </div>
          }
        >
          <DropdownItem onClick={() => setSelectedTransmission(null)}>
            All Transmissions
          </DropdownItem>
          {transmissionTypes.map((type) => (
            <DropdownItem
              key={type}
              onClick={() => handleTransmissionType(type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </DropdownItem>
          ))}
        </Dropdown>
      </div>

      <VariantBlock
        variants={filteredVariants}
        updateVariantDistance={updateVariantDistance}
        getVariantDistance={getVariantDistance}
        generateVariantKey={generateVariantKey}
        modelName={modelName}
        makeName={makeName}
        isSellable={isSellable}
        cityName={cityName}
        carAvailability={carAvailability}
      />
    </>
  );
}
